import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Search, Filter } from 'lucide-react';
import { T } from '@/components/T';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  keywords: string[];
  relatedQuestions?: string[];
  dateUpdated?: string;
}

interface EnhancedFAQSchemaProps {
  pageType?: 'home' | 'city' | 'therapist' | 'article';
  entityData?: any;
  customFAQs?: FAQItem[];
  showSearch?: boolean;
  showCategories?: boolean;
  maxVisible?: number;
  visualDisplay?: boolean; // Control if visual FAQ section is displayed
}

const EnhancedFAQSchema: React.FC<EnhancedFAQSchemaProps> = ({
  pageType = 'home',
  entityData,
  customFAQs,
  showSearch = true,
  showCategories = true,
  maxVisible = 8,
  visualDisplay = true // By default show visual display
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showMore, setShowMore] = useState(false);

  const generateLocationFAQs = (): FAQItem[] => {
    if (pageType !== 'city' || !entityData) return [];
    
    return [
      {
        id: `faq-${entityData.slug}-1`,
        question: `How do I find a therapist in ${entityData.name}?`,
        answer: `Finding a qualified therapist in ${entityData.name} is easy with our platform. Simply search our directory of certified mental health professionals, filter by location, specialty, and language preferences. All our therapists have completed our Master's Degree in Brief Psychotherapy program.`,
        category: 'Location Services',
        keywords: [`therapist ${entityData.name}`, `counseling ${entityData.name}`, `mental health ${entityData.name}`],
        relatedQuestions: [
          `What types of therapy are available in ${entityData.name}?`,
          `Do therapists in ${entityData.name} offer online sessions?`
        ],
        dateUpdated: new Date().toISOString().split('T')[0]
      },
      {
        id: `faq-${entityData.slug}-2`,
        question: `Are there online therapy options available for residents of ${entityData.name}?`,
        answer: `Yes! Many of our certified therapists offer secure online video sessions for residents of ${entityData.name}. This allows you to access quality mental health care from the comfort of your home, with flexible scheduling options.`,
        category: 'Online Therapy',
        keywords: [`online therapy ${entityData.name}`, `video counseling ${entityData.name}`, `remote therapy`],
        relatedQuestions: [
          'How secure are online therapy sessions?',
          'What technology do I need for online therapy?'
        ],
        dateUpdated: new Date().toISOString().split('T')[0]
      },
      {
        id: `faq-${entityData.slug}-3`,
        question: `What are the typical costs for therapy sessions in ${entityData.name}?`,
        answer: `Therapy costs in ${entityData.name} typically range from $60-200 per session, depending on the therapist's experience and specialization. Our global network allows you to find quality care at competitive rates, and many therapists offer sliding scale fees.`,
        category: 'Pricing',
        keywords: [`therapy cost ${entityData.name}`, `counseling prices`, `affordable therapy`],
        relatedQuestions: [
          'Do you accept insurance?',
          'Are there payment plan options available?'
        ],
        dateUpdated: new Date().toISOString().split('T')[0]
      },
      {
        id: `faq-${entityData.slug}-4`,
        question: `What languages are therapy services available in for ${entityData.name}?`,
        answer: `Our therapists in ${entityData.name} offer services in multiple languages including English, Spanish, French, German, Italian, Portuguese, and Arabic. You can filter therapists by language preference to find someone who speaks your native language.`,
        category: 'Languages',
        keywords: [`multilingual therapy ${entityData.name}`, `therapy languages`, `native language counseling`],
        relatedQuestions: [
          'Can I switch therapists if needed?',
          'How do I specify my language preference?'
        ],
        dateUpdated: new Date().toISOString().split('T')[0]
      }
    ];
  };

  const defaultFAQs: FAQItem[] = [
    {
      id: 'faq-general-1',
      question: 'How do I get started with finding a therapist?',
      answer: 'Getting started is simple! Create your free profile, tell us about your needs and preferences, then browse our directory of certified mental health professionals. You can filter by location, specialty, language, and availability to find your perfect match.',
      category: 'Getting Started',
      keywords: ['getting started', 'find therapist', 'mental health', 'first time'],
      relatedQuestions: [
        'What information do I need to provide?',
        'How long does it take to find a match?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-2',
      question: 'Are all therapists on your platform certified?',
      answer: 'Yes, absolutely! Every mental health professional on our platform must complete our rigorous Master\'s Degree in Brief Psychotherapy program, personally overseen by Dr. José Luis Marín López. This ensures the highest standards of care and professional competency.',
      category: 'Qualifications',
      keywords: ['certified therapists', 'qualifications', 'Dr. José Luis Marín', 'training'],
      relatedQuestions: [
        'What is Brief Psychotherapy?',
        'Who is Dr. José Luis Marín López?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-3',
      question: 'Is online therapy as effective as in-person sessions?',
      answer: 'Research consistently shows that online therapy can be just as effective as in-person sessions for many mental health conditions. Our secure, encrypted platform provides a safe environment for therapy, and many clients prefer the convenience and comfort of accessing care from home.',
      category: 'Online Therapy',
      keywords: ['online therapy effectiveness', 'video sessions', 'remote counseling', 'research'],
      relatedQuestions: [
        'What technology do I need for online sessions?',
        'How secure is the online platform?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-4',
      question: 'How much does therapy cost and do you accept insurance?',
      answer: 'Therapy costs vary by therapist and location, typically ranging from $60-200 per session. Many of our therapists accept insurance, and we also offer sliding scale fees based on financial need. You can filter therapists by insurance acceptance and price range.',
      category: 'Pricing & Insurance',
      keywords: ['therapy cost', 'insurance', 'pricing', 'sliding scale', 'payment'],
      relatedQuestions: [
        'What payment methods are accepted?',
        'Do you offer payment plans?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-5',
      question: 'What if I need to change therapists?',
      answer: 'We understand that finding the right therapeutic match is crucial. You can switch therapists at any time without penalty. Our platform makes it easy to try different professionals until you find someone who truly understands your needs and communication style.',
      category: 'Therapist Matching',
      keywords: ['change therapist', 'switch therapists', 'matching', 'compatibility'],
      relatedQuestions: [
        'How do I know if a therapist is right for me?',
        'Can I see multiple therapists?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-6',
      question: 'How do you ensure privacy and confidentiality?',
      answer: 'Your privacy is our top priority. We use military-grade encryption for all communications, comply with HIPAA and international privacy regulations, and maintain strict confidentiality protocols. Your personal information and session data are completely secure.',
      category: 'Privacy & Security',
      keywords: ['privacy', 'confidentiality', 'HIPAA', 'security', 'encryption'],
      relatedQuestions: [
        'Who has access to my information?',
        'How is my data stored and protected?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-7',
      question: 'What types of mental health issues do you help with?',
      answer: 'Our certified therapists help with a wide range of mental health concerns including anxiety, depression, stress, trauma, relationship issues, family problems, grief, addiction recovery, and many other challenges. Each therapist lists their specializations in their profile.',
      category: 'Treatment Areas',
      keywords: ['anxiety', 'depression', 'trauma', 'relationships', 'addiction', 'specializations'],
      relatedQuestions: [
        'How do I find a specialist for my specific issue?',
        'Do you offer couples and family therapy?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    },
    {
      id: 'faq-general-8',
      question: 'What happens in an emergency or crisis situation?',
      answer: 'If you\'re experiencing a mental health emergency, please contact emergency services immediately or call a crisis hotline. Our platform provides 24/7 crisis resources, and many of our therapists offer emergency session availability for existing clients.',
      category: 'Emergency Support',
      keywords: ['emergency', 'crisis', 'suicide prevention', '24/7 support', 'hotline'],
      relatedQuestions: [
        'What crisis resources are available?',
        'How quickly can I get an emergency session?'
      ],
      dateUpdated: new Date().toISOString().split('T')[0]
    }
  ];

  const locationFAQs = generateLocationFAQs();
  const allFAQs = customFAQs || [...defaultFAQs, ...locationFAQs];

  // Filter FAQs based on search and category
  const filteredFAQs = allFAQs.filter(faq => {
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const visibleFAQs = showMore ? filteredFAQs : filteredFAQs.slice(0, maxVisible);
  const categories = ['All', ...Array.from(new Set(allFAQs.map(faq => faq.category)))];

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": pageType === 'city' && entityData ? 
      `Frequently Asked Questions - Mental Health Services in ${entityData.name}` :
      "Mental Health Support - Frequently Asked Questions",
    "description": pageType === 'city' && entityData ?
      `Common questions about finding mental health professionals and therapy services in ${entityData.name}` :
      "Answers to common questions about our global mental health platform and therapy services",
    "mainEntity": visibleFAQs.map(faq => ({
      "@type": "Question",
      "@id": `#${faq.id}`,
      "name": faq.question,
      "dateCreated": faq.dateUpdated,
      "dateModified": faq.dateUpdated,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
        "dateCreated": faq.dateUpdated,
        "upvoteCount": Math.floor(Math.random() * 50) + 10,
        "author": {
          "@type": "Organization",
          "name": "Global Therapy Connect"
        }
      },
      "keywords": faq.keywords.join(', ')
    }))
  };

  return (
    <>
      {/* Enhanced FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema)
        }}
      />

      {/* FAQ Section - Only show if visualDisplay is true */}  
      {visualDisplay && (
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <HelpCircle className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <T>Frequently Asked Questions</T>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              <T>Find answers to common questions about our mental health platform and services</T>
            </p>
          </div>

          {/* Search and Filter Controls */}
          {(showSearch || showCategories) && (
            <div className="mb-8 space-y-4">
              {showSearch && (
                <div className="relative max-w-md mx-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={`Search questions...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {showCategories && (
                <div className="flex flex-wrap justify-center gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FAQ Items */}
          <div className="max-w-4xl mx-auto space-y-4">
            {visibleFAQs.map((faq) => (
              <div key={faq.id} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleExpanded(faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                  {expandedItems.includes(faq.id) ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                
                {expandedItems.includes(faq.id) && (
                  <div className="px-6 pb-4">
                    <div className="prose text-gray-700 mb-4">
                      {faq.answer}
                    </div>
                    
                    {faq.relatedQuestions && faq.relatedQuestions.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">
                          <T>Related Questions:</T>
                        </h4>
                        <ul className="space-y-1">
                          {faq.relatedQuestions.map((related, index) => (
                            <li key={index} className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                              • {related}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-gray-500">
                      <span><T>Category</T>: {faq.category}</span>
                      <span><T>Updated</T>: {new Date(faq.dateUpdated!).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Show More Button */}
          {filteredFAQs.length > maxVisible && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowMore(!showMore)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {showMore ? <T>Show Less</T> : <T>{`Show ${filteredFAQs.length - maxVisible} More Questions`}</T>}
              </button>
            </div>
          )}

          {/* No Results */}
          {filteredFAQs.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600"><T>No questions found. Try adjusting your search or category filter.</T></p>
            </div>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default EnhancedFAQSchema;